/*
  QuickBooks Desktop next to the platform's invoices — READ ONLY (owner decision 9 Oct 2026; src/lib/qbDesktop.js,
  backend/qbd_match.py). Not the older QuickBooks Online push (QuickBooksControls.js): this only shows what the
  company's QuickBooks has for an invoice, as read by the Web Connector; nothing is written anywhere.

  qbMatchKey(id, variation)     'invoice:<id>' | 'variation_invoice:<id>' — the keys of a `matches` map
  qbMatchDiffers(match)         QuickBooks disagrees: paid / still open there, another amount or another customer
  qbMatchTitle(match)           every note as words, then the QuickBooks invoice (number, date, total, balance)
  QbMatchChip                   one small StatusPill: "Paid in QuickBooks", "Matches QuickBooks #275", …
  useQbInvoiceMatches(enabled)  GET /qb-desktop/status, then /invoice-matches once when QuickBooks has been read;
                                null while off / not read yet / when a request fails (the page shows nothing extra)
*/
import { useEffect, useState } from 'react';
import { StatusPill } from '@/components/ui/status-pill';
import { formatDate, formatNumber } from '@/lib/format';
import {
  QB_NOTE_TEXT, fetchQbInvoiceMatches, fetchQbStatus, qbDataReady, qbMatchLabel, qbMatchTone,
} from '@/lib/qbDesktop';

const money = (value) => formatNumber(value, { decimals: 2 });

/** The notes the "Differs from QuickBooks" filter looks for. */
export const QB_DIFFERS_NOTES = ['paid_in_quickbooks', 'open_in_quickbooks', 'amount_differs', 'other_customer'];

export const qbMatchKey = (id, variation = false) => `${variation ? 'variation_invoice' : 'invoice'}:${id}`;

export const qbMatchDiffers = (match) => (match?.notes || []).some((note) => QB_DIFFERS_NOTES.includes(note));

/** Tooltip of a chip: what differs in words, then the QuickBooks invoice it was compared with. */
export function qbMatchTitle(match) {
  if (!match) return '';
  const notes = match.notes || [];
  const words = notes.length ? notes.map((note) => QB_NOTE_TEXT[note] || note).join(' · ') : 'Amount and payment agree with QuickBooks';
  let detail = '';
  if (match.ambiguous) {
    detail = `${formatNumber(match.ambiguous)} QuickBooks invoices have number ${match.ref_number || 'this number'}`;
  } else if (match.qb_id || match.ref_number) {
    detail = [
      match.ref_number ? `QuickBooks #${match.ref_number}` : 'QuickBooks invoice',
      match.txn_date ? formatDate(match.txn_date) : '',
      match.total !== undefined && match.total !== null ? `total ${money(match.total)}` : '',
      match.balance_remaining !== undefined && match.balance_remaining !== null ? `balance ${money(match.balance_remaining)}` : '',
      match.customer || '',
    ].filter(Boolean).join(' · ');
  }
  return [words, detail].filter(Boolean).join(' — ');
}

/** The QuickBooks side of one platform invoice; nothing when there is no match to show. */
export function QbMatchChip({ match, className, testId }) {
  if (!match) return null;
  return (
    <StatusPill
      tone={qbMatchTone(match)}
      size="sm"
      label={qbMatchLabel(match)}
      title={qbMatchTitle(match)}
      className={className}
      data-testid={testId}
    />
  );
}

/** GET /invoice-matches once, only when QuickBooks Desktop is on and has been read (else null). */
export function useQbInvoiceMatches(enabled) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetchQbStatus()
      .then((status) => (qbDataReady(status) ? fetchQbInvoiceMatches() : null))
      .then((result) => {
        if (alive) setData(result && result.matches && typeof result.matches === 'object' ? result : null);
      })
      // an extra next to the register: without it the page works as before
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [enabled]);
  return data;
}
